/**
 * Step 13 F-2: decisions behind the global project workspace nav (Header.vue) and the job
 * candidates page's `workspace` query. Pure (relative imports only) so `node --test` loads it.
 *
 * V2 jobs have two tabs, Agent (V2 page) and Job Talent; legacy jobs keep Agent (old page) /
 * Talent Recs / Job Talent. A job that is not in the job store yet renders as V2 (missing-field
 * rule), but irreversible URL changes (dropping `workspace=talentRecommend`) wait until the job is
 * actually known, so a legacy job opened before the list loads keeps its Talent Recs view.
 */
import {
  getAgentEntryRoute,
  getAgentRouteJobId,
  isAgentRouteName,
  isLegacyAgentJob,
  type AgentEntryJob,
  type AgentEntryRoute,
} from './agentEntry.ts'

export type JobWorkspaceTab = 'agent' | 'talentRecommend' | 'jobTalent'

export const JOB_CANDIDATES_ROUTE_NAME = 'JobCandidates'
export const TALENT_RECOMMEND_WORKSPACE = 'talentRecommend'

const LEGACY_TABS: readonly JobWorkspaceTab[] = ['agent', 'talentRecommend', 'jobTalent']
const V2_TABS: readonly JobWorkspaceTab[] = ['agent', 'jobTalent']

type QueryRecord = Record<string, unknown>
type RouteLike = { name?: unknown; params?: QueryRecord; query?: QueryRecord }

const normalize = (value: unknown) => {
  const picked = Array.isArray(value) ? value[0] : value
  return picked === null || picked === undefined ? '' : String(picked).trim()
}

/** The job the nav is about: `/manage/jobs/:jobId` or an Agent page's `?jobId=`. */
export const getWorkspaceJobId = (route: RouteLike) =>
  normalize(route.params?.jobId) || normalize(route.query?.jobId)

export const shouldShowJobWorkspaceNav = (route: RouteLike) =>
  route.name === JOB_CANDIDATES_ROUTE_NAME || Boolean(getAgentRouteJobId(route))

export const getJobWorkspaceTabs = (job: AgentEntryJob | null | undefined): JobWorkspaceTab[] => [
  ...(isLegacyAgentJob(job) ? LEGACY_TABS : V2_TABS),
]

/** A known (loaded) V2 job opened with `workspace=talentRecommend`: drop the param, show Job Talent. */
export const shouldDropTalentRecommendWorkspace = (workspace: unknown, job: AgentEntryJob | null | undefined) =>
  normalize(workspace) === TALENT_RECOMMEND_WORKSPACE && Boolean(job) && !isLegacyAgentJob(job)

/** The job candidates page's own view: Talent Recs or Job Talent. */
export const resolveJobCandidatesWorkspace = (
  workspace: unknown,
  job: AgentEntryJob | null | undefined,
): Exclude<JobWorkspaceTab, 'agent'> =>
  normalize(workspace) === TALENT_RECOMMEND_WORKSPACE && !shouldDropTalentRecommendWorkspace(workspace, job)
    ? 'talentRecommend'
    : 'jobTalent'

export const resolveActiveJobWorkspaceTab = (
  route: RouteLike,
  job: AgentEntryJob | null | undefined,
): JobWorkspaceTab =>
  isAgentRouteName(route.name) ? 'agent' : resolveJobCandidatesWorkspace(route.query?.workspace, job)

export type JobCandidatesRoute = {
  name: typeof JOB_CANDIDATES_ROUTE_NAME
  params: { jobId: string }
  query: Record<string, string | undefined>
}

/**
 * Project dropdown: on an Agent page (old or V2) go to the target job's own Agent page (a target not
 * in the store counts as V2); on the job candidates page keep the current filter / pipeline /
 * workspace (unchanged behavior), except that a Talent Recs view never carries over to a known V2
 * target.
 */
export const getProjectSwitchRoute = ({
  route,
  targetJobId,
  targetJob,
  profile,
}: {
  route: RouteLike
  targetJobId: string
  targetJob: AgentEntryJob | null | undefined
  profile?: string
}): AgentEntryRoute | JobCandidatesRoute => {
  if (isAgentRouteName(route.name)) {
    return getAgentEntryRoute(targetJob ?? { id: targetJobId }, { profile })
  }
  const workspace = normalize(route.query?.workspace)
  return {
    name: JOB_CANDIDATES_ROUTE_NAME,
    params: { jobId: targetJobId },
    query: {
      filter: normalize(route.query?.filter) || 'all',
      pipeline: normalize(route.query?.pipeline) || undefined,
      // An unknown target keeps the param; the job candidates page drops it once the job is known.
      workspace: workspace && !shouldDropTalentRecommendWorkspace(workspace, targetJob) ? workspace : undefined,
      profile,
    },
  }
}

/**
 * Tab click. Agent → the job's Agent page (by its version). Talent Recs / Job Talent → the job
 * candidates page, keeping the current query (unchanged behavior).
 */
export const getWorkspaceTabRoute = ({
  tab,
  route,
  jobId,
  job,
  profile,
  now = Date.now(),
}: {
  tab: JobWorkspaceTab
  route: RouteLike
  jobId: string
  job: AgentEntryJob | null | undefined
  profile?: string
  now?: number
}): AgentEntryRoute | JobCandidatesRoute | null => {
  if (tab === 'agent') {
    return getAgentEntryRoute(job ?? (jobId ? { id: jobId } : null), { profile })
  }
  if (!jobId) return null
  const query: Record<string, string | undefined> = {}
  for (const [key, value] of Object.entries(route.query ?? {})) {
    const normalized = normalize(value)
    if (normalized) query[key] = normalized
  }
  return {
    name: JOB_CANDIDATES_ROUTE_NAME,
    params: { jobId },
    query: {
      ...query,
      jobId: undefined,
      workspace: tab === 'talentRecommend' ? TALENT_RECOMMEND_WORKSPACE : undefined,
      refresh: tab === 'jobTalent' ? String(now) : query.refresh,
    },
  }
}

/**
 * The project button's label. For a job in the store that is not legacy (V2), the store's name wins:
 * step 12 refreshes the store after a run renames the job, and the URL's `profile` would be stale.
 * Legacy jobs keep the old URL/cache-based label.
 */
export const resolveWorkspaceJobLabel = ({
  job,
  storeDisplayName,
  routeProfile,
  fallback,
}: {
  job: AgentEntryJob | null | undefined
  storeDisplayName: string
  routeProfile: string
  fallback: string
}) => {
  if (job && !isLegacyAgentJob(job) && storeDisplayName) return storeDisplayName
  return routeProfile || fallback
}
